import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { countingApi } from '../../api/countingApi';
import { isHandsUnavailable } from '../../api/handsUnavailable';
import { DoorCard } from './DoorCard';

/**
 * The Counting entry on the dashboard.
 *
 * Counting has no plain URL to link to: /counting-practice expects the session it should
 * run to be handed over in router state and bounces back to the dashboard without it. So
 * the card starts the session first and then navigates — the same flow the Practice hub's
 * Counting button used before Counting moved onto the dashboard.
 */
export function CountingDoorCard() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePractice = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await countingApi.startCountingSession();

      if (response.problems.length === 0) {
        throw new Error("No more unseen counting questions. If you've been practicing, clear your problem history on the Account page to see them again.");
      }

      navigate('/counting-practice', {
        state: {
          problems: response.problems,
          totalAvailable: response.totalAvailable,
          sessionOrigin: 'practice-counting',
        },
      });
    } catch (err) {
      // Spec 030: a broken tournament-hand link is a retryable fault, never "no more unseen
      // questions" — clearing problem history cannot fix it.
      setError(
        isHandsUnavailable(err)
          ? 'Counting practice could not be loaded. Please try again.'
          : err instanceof Error
            ? err.message
            : 'An error occurred. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  return (
    <DoorCard
      onClick={handlePractice}
      busy={isLoading}
      icon="&#129518;"
      title="Counting"
      description="Read the hands you cannot see. Follow the shape, the honours and the tempo trick by trick, until the whole deal lies face up in your head."
      cta={isLoading ? 'Dealing the cards' : 'Start counting'}
      notice={
        error && (
          <p className="text-danger">
            {error}{' '}
            <button
              type="button"
              onClick={handlePractice}
              className="underline hover:text-danger-hover"
            >
              Try again
            </button>
          </p>
        )
      }
    />
  );
}
